import { FC, isValidElement, ReactNode, useState } from 'react'
import { FieldErrors } from 'react-hook-form'

import { useDeepCompareEffect } from 'ahooks'
import cx from 'clsx'
import { cloneDeep } from 'lodash'
import { isObject, toArray } from 'packages/helper'
import { SlideToggle } from 'ui/SlideToggle'

import classes from './ErrorStack.module.scss'

interface ErrorStackProps {
  className?: string
  classNameWrap?: string
  errors?: string[] | string | FieldErrors | ReactNode
  isAnimation?: boolean
}

export const ErrorStack: FC<ErrorStackProps> = ({ className, classNameWrap, errors, isAnimation }) => {
  const [showError, setShowError] = useState(false)
  const [errorsArray, setErrorsArray] = useState<(string | ReactNode)[]>([])

  useDeepCompareEffect(() => {
    let s1: NodeJS.Timeout
    let s2: NodeJS.Timeout

    const errorsInternal = (
      isValidElement(errors)
        ? [errors]
        : typeof errors === 'string'
        ? [errors.trim()]
        : isObject(errors)
        ? Object.values(errors)
            .map(({ message }) => message)
            .filter((message): message is string => !!message?.trim())
        : errors
    ) as (string | ReactNode)[]

    if (errorsInternal && toArray(errorsInternal)?.length) {
      setErrorsArray(errorsInternal)
      if (!isAnimation) {
        setShowError(true)
      } else {
        s1 = setTimeout(() => setShowError(true))
      }
    } else {
      if (showError && isAnimation) {
        s2 = setTimeout(() => setErrorsArray([]), 200)
      } else {
        setErrorsArray([])
      }
      setShowError(false)
    }

    return () => {
      clearTimeout(s1)
      clearTimeout(s2)
    }
  }, [cloneDeep(errors)])

  return (
    <SlideToggle className={classNameWrap} duration={isAnimation ? 200 : 0} open={showError}>
      <div className={cx(classes.wrap, className)}>
        {errorsArray.map((message, i) => (
          <div className={classes.message} key={i}>
            {message}
          </div>
        ))}
      </div>
    </SlideToggle>
  )
}
